// src/components/UnifiedLeagueList.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import leagueService from '../services/leagueService';
import { copyInviteLink } from './InviteUtils';
import { AuthContext } from './AuthContext';
import LeagueTable from './LeagueTable';
import { toast } from 'sonner';
import { trackEvent } from '../utils/analytics';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SEO from './SEO';

const UnifiedLeagueList = ({
	mode = 'my',                       // 'my' | 'public'
	computeUserRankings,               // default podle mode
	showPrivateLeaguesSection,         // default podle mode
	emptyMessageKey,                   // default podle mode
	onJoinLeague,                      // jen pro public
	onLeaveLeague,                     // callback for leave league
}) => {
	const [leagues, setLeagues] = useState([]);
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(true);
	const [openMenuId, setOpenMenuId] = useState(null);

	// Leave league confirmation dialog state
	const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
	const [leagueToLeave, setLeagueToLeave] = useState(null);

	// volitelný „progres" styl jako v LeagueList.js (jen když dopočítáváme žebříčky)
	const [loadingProgress, setLoadingProgress] = useState(0);
	const [loadingStep, setLoadingStep] = useState('');

	const { t } = useTranslation();
	const { userName } = useContext(AuthContext);
	const navigate = useNavigate();

	const effectiveComputeUserRankings =
		typeof computeUserRankings === 'boolean' ? computeUserRankings : mode === 'my';
	const effectiveShowPrivate =
		typeof showPrivateLeaguesSection === 'boolean' ? showPrivateLeaguesSection : mode === 'my';
	const effectiveEmptyKey =
		emptyMessageKey || (mode === 'my' ? 'leagues.noLeagueMember' : 'leagues.noPublicLeaguesAvailable');

	useEffect(() => {
		let ignore = false; // Příznak pro ošetření souběhu asynchronních operací (race conditions)

		const fetchLeagues = async () => {
			try {
				setLoading(true);
				setError('');
				if (effectiveComputeUserRankings) {
					setLoadingProgress(0);
					setLoadingStep(t('loadings.fetchingLeagues', { defaultValue: 'Načítání seznamu lig...' }));
				}

				const base = mode === 'public'
					? await leagueService.getPublicLeagues()        // PublicLeagueList.js
					: await leagueService.getMyLeagues();           // LeagueList.js

				if (ignore) return; // Pokud se efekt mezitím restartoval, nepokračujeme

				if (!effectiveComputeUserRankings) {
					setLeagues(base);
					setLoading(false);
					return;
				}

				// Logika pro výpočet žebříčků (kopírováno z LeagueList.js)
				setLoadingProgress(40);
				setLoadingStep(t('loadings.fetchingRankings', { defaultValue: 'Načítání žebříčků...' }));

				const total = base.length || 1;
				const enriched = [];

				for (let i = 0; i < base.length; i++) {
					if (ignore) return; // Kontrola i uvnitř cyklu před každým asynchronním voláním

					const league = base[i];
					const current = 40 + (i / total) * 40;
					setLoadingProgress(Math.round(current));

					try {
						const rankings = await leagueService.getLeagueRankings(league.id);
						// Výpočet pořadí uživatele
						let userRanking = null, lastPoints = null, lastRank = 0, realIndex = 0;
						for (let r = 0; r < rankings.length; r++) {
							const p = rankings[r];
							realIndex++;
							const same = p.totalPoints === lastPoints;
							const rank = same ? lastRank : realIndex;
							if (p.username === userName) { userRanking = rank; break; }
							lastPoints = p.totalPoints;
							lastRank = rank;
						}
						enriched.push({ ...league, userRanking, totalPlayers: rankings.length });
					} catch {
						enriched.push({ ...league, userRanking: null, totalPlayers: league.players?.length || 0 });
					}
				}

				if (ignore) return;

				setLoadingStep(t('loadings.finalizing', { defaultValue: 'Dokončování...' }));
				setLoadingProgress(100);
				setLeagues(enriched);
				setLoading(false);
			} catch (e) {
				if (ignore) return;
				console.error('Failed to fetch leagues:', e);
				setError(t('errors.failedToFetchLeagues', { defaultValue: 'Failed to fetch leagues' }));
				setLoading(false);
			}
		};

		// Pro „my" potřebujeme znát userName, stejně jako v LeagueList.js
		if (mode === 'my' && !userName) return;

		fetchLeagues();

		// Cleanup funkce: nastaví ignore na true, pokud se změní závislosti nebo se komponenta odpojí
		return () => {
			ignore = true;
		};
	}, [mode, userName, t, effectiveComputeUserRankings]);

	const handleLeagueClick = (leagueId) => navigate(`/league/${leagueId}`);

	const handleInvitePlayers = async (leagueId) => {
		if (mode !== 'my') return;
		try {
			await copyInviteLink(leagueId, t);
			setOpenMenuId(null);
			toast.success(t('leagues.inviteLinkCopied', { defaultValue: 'Pozvánka byla zkopírována do schránky!' }));
		} catch (error) {
			console.error('Failed to copy invite link:', error);
			toast.error(t('errors.failedToCopyInvite', { defaultValue: 'Nepodařilo se zkopírovat pozvánku' }));
		}
	};

	const handleJoinLeague = async (leagueId) => {
		try {
			setOpenMenuId(null);

			const result = await leagueService.joinLeague(leagueId);

			if (result.message || result.success) {
				// Update the local state to reflect the change
				setLeagues(prev => prev.map(l =>
					l.id === leagueId
						? { ...l, isMember: true, players: [...(l.players || []), { username: userName }] }
						: l
				));

				toast.success(t('leagueDetails.successfullyJoinedLeague', { defaultValue: 'Úspěšně jste se připojili do ligy!' }));

				// Call the optional callback
				if (onJoinLeague) {
					onJoinLeague(leagueId);
				}

				// Navigate to the league after successful join
				setTimeout(() => {
					navigate(`/league/${leagueId}`);
				}, 1000); // Small delay to let user see the success message
			} else if (result.error) {
				toast.error(result.error);
			}
		} catch (error) {
			console.error('Failed to join league:', error);

			// Handle different types of errors
			let errorMessage = t('errors.failedToJoinLeague', { defaultValue: 'Nepodařilo se připojit do ligy' });

			if (error.response?.status === 400) {
				errorMessage = error.response.data?.error || errorMessage;
			} else if (error.response?.status === 401) {
				errorMessage = t('errors.unauthorized', { defaultValue: 'Nejste přihlášeni' });
			} else if (error.response?.status === 403) {
				errorMessage = t('errors.forbidden', { defaultValue: 'Nemáte oprávnění k této akci' });
			} else if (error.response?.status === 404) {
				errorMessage = t('errors.leagueNotFound', { defaultValue: 'Liga nebyla nalezena' });
			}

			toast.error(errorMessage);
		}
	};

	const handleLeaveLeague = async (leagueId) => {
		try {
			const result = await leagueService.leaveLeague(leagueId);

			if (result.message || result.success) {
				if (mode === 'my') {
					setLeagues(prev => prev.filter(l => l.id !== leagueId));
				} else {
					setLeagues(prev => prev.map(l =>
						l.id === leagueId
							? {
								...l,
								isMember: false,
								userRanking: null,
								players: l.players ? l.players.filter(p => p.username !== userName) : []
							}
							: l
					));
				}

				toast.success(t('leagues.successfullyLeftLeague', {
					defaultValue: 'Úspěšně jste opustili ligu!'
				}));

				if (onLeaveLeague) {
					onLeaveLeague(leagueId);
				}
			} else if (result.error) {
				toast.error(result.error);
			}
		} catch (error) {
			console.error('Failed to leave league:', error);
			toast.error(t('errors.failedToLeaveLeague'));
		}
	};

	const handleConfirmLeave = async () => {
		if (!leagueToLeave) return;

		try {
			const result = await leagueService.leaveLeague(leagueToLeave.id);

			if (result.message || result.success) {
				// Update the local state to remove the league or mark user as not a member
				if (mode === 'my') {
					// In "my leagues" mode, remove the league from the list
					setLeagues(prev => prev.filter(l => l.id !== leagueToLeave.id));
				} else {
					// In other modes, just mark as not a member
					setLeagues(prev => prev.map(l =>
						l.id === leagueToLeave.id
							? {
								...l,
								isMember: false,
								userRanking: null,
								players: l.players ? l.players.filter(p => p.username !== userName) : []
							}
							: l
					));
				}

				toast.success(t('leagues.successfullyLeftLeague', {
					defaultValue: 'Úspěšně jste opustili ligu!'
				}));

				// Call the optional callback
				if (onLeaveLeague) {
					onLeaveLeague(leagueToLeave.id);
				}
			} else if (result.error) {
				toast.error(result.error);
			}
		} catch (error) {
			console.error('Failed to leave league:', error);

			// Handle different types of errors
			let errorMessage = t('errors.failedToLeaveLeague', {
				defaultValue: 'Nepodařilo se opustit ligu'
			});

			if (error.response?.status === 400) {
				errorMessage = error.response.data?.error || errorMessage;
			} else if (error.response?.status === 401) {
				errorMessage = t('errors.unauthorized', { defaultValue: 'Nejste přihlášeni' });
			} else if (error.response?.status === 403) {
				errorMessage = t('errors.forbidden', { defaultValue: 'Nemáte oprávnění k této akci' });
			} else if (error.response?.status === 404) {
				errorMessage = t('errors.leagueNotFound', { defaultValue: 'Liga nebyla nalezena' });
			}

			toast.error(errorMessage);
		} finally {
			setLeaveConfirmOpen(false);
			setLeagueToLeave(null);
		}
	};

	const handleCancelLeave = () => {
		setLeaveConfirmOpen(false);
		setLeagueToLeave(null);
	};

	// Enhanced empty state for dashboard
	const renderEmptyState = () => {
		if (mode === 'my') {
			return (
				<div className="dashboard-empty-state">
					<div className="empty-state-content">
						<div className="empty-state-icon">🏆</div>
						<h3 className="empty-state-title">
							{t('noLeaguesTitle', { userName: userName })}
						</h3>
						<p className="empty-state-description">
							{t('noLeaguesDescription')}
						</p>
						<div className="empty-state-actions">
							<button
								className="btn btn-primary"
								onClick={() => navigate('/public-leagues')}
							>
								{t('viewPublicLeagues')}
							</button>
							<button
								className="btn btn-primary"
								onClick={() => navigate('/create-league')}
							>
								{t('createPrivateLeague')}
							</button>
						</div>
					</div>
				</div>
			);
		}

		// Default empty state for other modes
		return (
			<div className="leagues-container">
				<div className="leagues-empty">
					<p>{t(effectiveEmptyKey)}</p>
				</div>
			</div>
		);
	};

	// stejný „fancy" loading jako v LeagueList.js, ale jen když opravdu počítáme žebříčky
	if (loading && effectiveComputeUserRankings) {
		return (
			<div className="leagues-container">
				<div className="leagues-loading">
					<div className="loading-content">
						<div className="loading-info">
							<p className="loading-text">{loadingStep}</p>
							<div className="progress-bar">
								<div className="progress-fill" style={{ width: `${loadingProgress}%` }} />
							</div>
							<p className="progress-percentage">{loadingProgress}%</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Show enhanced empty state if no leagues
	if (!loading && !error && leagues.length === 0) {
		return renderEmptyState();
	}

	const pageTitle = mode === 'my'
		? t('dashboard.title', { defaultValue: "Dashboard" })
		: t('publicLeagues.title', { defaultValue: "Veřejné ligy" });

	return (
		<>
			<SEO
				title={pageTitle}
				description={mode === 'public' ? "Připoj se do veřejných lig a porovnej své tipy s ostatními." : "Spravuj své ligy a sleduj výsledky."}
			/>
			<LeagueTable
				leagues={leagues}
				loading={loading}
				error={error}
				onLeagueClick={handleLeagueClick}
				onInvitePlayers={mode === 'my' ? handleInvitePlayers : undefined}
				onJoinLeague={mode === 'public' ? handleJoinLeague : undefined}
				onLeaveLeague={handleLeaveLeague}
				openMenuId={openMenuId}
				setOpenMenuId={setOpenMenuId}
				userName={userName}
				showPrivateLeaguesSection={effectiveShowPrivate}
				emptyMessageKey={effectiveEmptyKey}
			/>

			{/* Leave League Confirmation Dialog */}
			<Dialog
				open={leaveConfirmOpen}
				onClose={handleCancelLeave}
				maxWidth="sm"
				fullWidth
				PaperProps={{
					sx: {
						borderRadius: 2,
					}
				}}
			>
				<DialogTitle sx={{ m: 0, p: 2, pb: 1 }}>
					{t('leagues.confirmLeaveTitle')}
					<IconButton
						aria-label="close"
						onClick={handleCancelLeave}
						sx={{
							position: 'absolute',
							right: 8,
							top: 8,
							color: (theme) => theme.palette.grey[500],
						}}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent sx={{ pt: 1 }}>
					<Typography variant="body1" sx={{ mb: 1 }}>
						{t('leagues.confirmLeaveMessage')}
					</Typography>
					{leagueToLeave && (
						<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
							<strong>{leagueToLeave.name}</strong>
						</Typography>
					)}
				</DialogContent>
				<DialogActions sx={{ p: 2, pt: 1 }}>
					<Button
						onClick={handleCancelLeave}
						variant="outlined"
						sx={{ mr: 1 }}
					>
						{t('cancel')}
					</Button>
					<Button
						onClick={handleConfirmLeave}
						variant="contained"
						color="error"
						autoFocus
					>
						{t('leagues.leaveLeague', { defaultValue: 'Opustit ligu' })}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default UnifiedLeagueList;